import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Notification,
    Page,
    PageContent,
    Text,
    TextInput,
} from 'grommet';
import { StatusCritical, StatusGood } from 'grommet-icons';
import { redirect, useLoaderData, useNavigate } from 'react-router-dom';
import { API, AppConstants, hasToken, hasUserId, UserService } from '@enrich/shared-lib';

const defaultValues = { password: '', confirmPassword: '' };

export const ChangePassword = () => {
    const user = useLoaderData();
    const navigate = useNavigate();

    const [value, setValue] = useState(defaultValues);
    const [buttonBusy, setButtonBusy] = useState(false);
    const [matchError, setMatchError] = useState('');
    const [successToast, setSuccessToast] = useState(false);
    const [failureToast, setFailureToast] = useState({
        visible: false,
        message: '',
    });
    const redirectTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

    const handleSubmit = async () => {
        if (value.password !== value.confirmPassword) {
            setMatchError('Passwords do not match');
            return;
        }
        setMatchError('');
        setButtonBusy(true);
        try {
            await API.put('/api/users/me', {
                password: value.password,
                confirmPassword: value.confirmPassword,
            });
            setSuccessToast(true);
            redirectTimerRef.current = setTimeout(
                () => navigate('/welcome-calls'),
                2000,
            );
        } catch (error) {
            console.log(error);
            setFailureToast({
                visible: true,
                message: error?.response?.data?.error?.message ?? error?.message ?? '',
            });
        }
        setButtonBusy(false);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent align="center">
                <Box width="medium">
                    <Heading level="2" margin={{ bottom: 'none' }}>
                        Enrich Coaching Platform
                    </Heading>
                    <Text margin={{ bottom: 'medium' }} color="dark-5">
                        Set a new password before continuing.
                    </Text>
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onSubmit={handleSubmit}>
                        <Box gap="medium">
                            <FormField
                                label="New Password"
                                name="password"
                                htmlFor="change-password-input"
                                required>
                                <TextInput
                                    id="change-password-input"
                                    name="password"
                                    type="password"
                                />
                            </FormField>
                            <FormField
                                label="Confirm Password"
                                name="confirmPassword"
                                htmlFor="change-confirm-password-input"
                                error={matchError}
                                required>
                                <TextInput
                                    id="change-confirm-password-input"
                                    name="confirmPassword"
                                    type="password"
                                />
                            </FormField>
                            <Button
                                type="submit"
                                primary
                                label="Change Password"
                                busy={buttonBusy}
                                margin={{ top: 'small' }}
                            />
                        </Box>
                    </Form>
                </Box>
            </PageContent>
            {successToast && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="normal"
                    time={8000}
                    title="Password changed successfully!"
                    icon={<StatusGood color="green" />}
                    onClose={() => setSuccessToast(false)}
                />
            )}
            {failureToast.visible && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="critical"
                    time={8000}
                    title="Failed to change password"
                    message={failureToast.message || undefined}
                    icon={<StatusCritical color="red" />}
                    onClose={() =>
                        setFailureToast({ visible: false, message: '' })
                    }
                />
            )}
        </Page>
    );
};

export async function changePasswordLoader() {
    if (!hasToken() || !hasUserId()) {
        return redirect('/login');
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    try {
        const user = await new UserService().getUser(userId);

        if (!user) return redirect('/login');

        if (user.tempPasswordChanged) return redirect('/');

        return user;
    } catch {
        return redirect('/login');
    }
}
